/**
 * TBLZ-2123: Growth - Trailblazers experiment - More info at go/trailblazerexperiments
 */
import { useEffect, useMemo, useRef } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { NAVIGATION_CHANNEL } from './analytics';
import { Product, Subproduct } from '../../types';
import { daysBetween } from './days-between';
import { TRACK_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { ConnectedSite } from './links';

export type Viewer = {
	product?: Product;
	locale?: string;
	subproduct?: Subproduct;
};

export type Cohort = 'variation' | 'control' | 'not-enrolled';

export type ExperimentNudgeToJira = {
	attributes?: {
		activationDate?: string;
		hasAdminAccess?: boolean;
		product?: string;
		subproduct?: Subproduct;
	};
	nudgeToJiraCohort?: Cohort;
};

const isWithinD1to6AI = (date?: string): boolean => {
	if (date === undefined) {
		return false;
	}

	const now = new Date();
	const compare = new Date(date);

	if (isNaN(compare.getTime())) {
		return false;
	}
	const days = daysBetween(now, compare);

	return now > compare && days >= 1 && days <= 6;
};

export interface IsEligibleForNudgeToJiraParams {
	connectedSite: ConnectedSite;
	cloudId?: string | null;
	viewer?: Viewer;
}

export const getIsEligibleForNudgeToJira = ({
	connectedSite,
	cloudId,
	viewer,
}: IsEligibleForNudgeToJiraParams) => {
	const { shouldShowNewLozenge, shouldShowTryLozenge, product, isCurrentSite } = connectedSite;
	const isExistingLozengeShown = shouldShowNewLozenge || shouldShowTryLozenge;

	if (
		product?.productType !== 'JIRA_SOFTWARE' ||
		isExistingLozengeShown || // Can't be eligible if existing lozenge is shown
		!viewer
	) {
		return false;
	}

	// Must be the current site, have a cloudId/tenantId and be viewed in EN
	const isEligibleViewer =
		isCurrentSite && cloudId !== undefined && (viewer.locale || '').toLowerCase().startsWith('en');

	return isEligibleViewer && isWithinD1to6AI(product.activationDate);
};

export const useNudgeToJira = (experimentNudgeToJira?: ExperimentNudgeToJira) => {
	const hasFiredExperiment = useRef<boolean>(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { nudgeToJiraCohort } = experimentNudgeToJira || {};
	const { activationDate, hasAdminAccess, product, subproduct } =
		experimentNudgeToJira?.attributes || {};

	const attributes = useMemo(
		() => ({
			activationDate,
			hasAdminAccess,
			product,
			subproduct,
		}),
		[activationDate, hasAdminAccess, product, subproduct],
	);

	useEffect(() => {
		if (
			createAnalyticsEvent &&
			!hasFiredExperiment.current &&
			nudgeToJiraCohort &&
			['control', 'variation'].includes(nudgeToJiraCohort)
		) {
			createAnalyticsEvent({
				eventType: TRACK_EVENT_TYPE,
				action: 'exposed',
				actionSubject: 'feature',
				actionSubjectId: 'nudgeToJiraLinkExperiment',
				attributes: {
					...attributes,
					flagKey: 'nudge_to_jira_in_switcher',
					cohort: nudgeToJiraCohort,
				},
			}).fire(NAVIGATION_CHANNEL);
			hasFiredExperiment.current = true;
		}
	}, [nudgeToJiraCohort, attributes, createAnalyticsEvent]);
};
